import React, { useState, useEffect } from 'react';
import { ReactComponent as CheckIcon } from '../../assets/icons/check.svg';
import { ReactComponent as CrossIcon } from '../../assets/icons/cross.svg';
import { ReactComponent as CloseIcon } from '../../assets/closing-arrow-round.svg'; // Ajoutez cette icône si disponible
import Button from '../Button/Button';
import './ResetPasswordModal.css';

interface ResetPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSendLink: (email: string) => void;
  onDirectReset: (userId: string, password: string) => void;
  userId: string;
  userEmail: string;
}

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({
  isOpen,
  onClose,
  onSendLink,
  onDirectReset,
  userId,
  userEmail
}) => {
  const [resetOption, setResetOption] = useState<'link' | 'direct'>('link');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Fermer le modal avec Escape
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  // Empêcher le scroll du body quand le modal est ouvert
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Password validation
  const hasLowerCase = /[a-z]/.test(newPassword);
  const hasUpperCase = /[A-Z]/.test(newPassword);
  const hasNumber = /\d/.test(newPassword);
  const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword);
  const isPasswordValid = hasLowerCase && hasUpperCase && hasNumber && hasSpecialChar;
  const passwordsMatch = newPassword === confirmPassword && newPassword !== '';

  const handleSubmit = async () => {
    setError('');
    setIsSubmitting(true);

    try {
      if (resetOption === 'link') {
        await onSendLink(userEmail);
      } else {
        if (!isPasswordValid) {
          setError('Le mot de passe ne respecte pas les critères de sécurité');
          setIsSubmitting(false);
          return;
        }

        if (!passwordsMatch) {
          setError('Les mots de passe ne correspondent pas');
          setIsSubmitting(false);
          return;
        }

        await onDirectReset(userId, newPassword);
      }
      onClose();
    } catch (err) {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Réinitialisation du mot de passe</h2>
        <button
          className="close-button"
          onClick={onClose}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 38 38" fill="none">
            <circle cx="19" cy="19" r="19" fill="#2C3948"/>
            <path d="M13 13L25 25M25 13L13 25" stroke="#FFFDFC" strokeWidth="3.5" strokeLinecap="round"/>
          </svg>
        </button>
        </div>

        <div className="modal-content">
          <div className="reset-password-modal">
            <div className="reset-options">
              <div
                className={`reset-option ${resetOption === 'link' ? 'active' : ''}`}
                onClick={() => setResetOption('link')}
              >
                <input
                  type="radio"
                  checked={resetOption === 'link'}
                  onChange={() => setResetOption('link')}
                />
                <label>Envoyer un lien de réinitialisation par email</label>
              </div>
              <div
                className={`reset-option ${resetOption === 'direct' ? 'active' : ''}`}
                onClick={() => setResetOption('direct')}
              >
                <input
                  type="radio"
                  checked={resetOption === 'direct'}
                  onChange={() => setResetOption('direct')}
                />
                <label>Définir un nouveau mot de passe directement</label>
              </div>
            </div>

            {resetOption === 'link' ? (
              <div className="email-info">
                <p>Un email avec un lien de réinitialisation sera envoyé à :</p>
                <strong>{userEmail}</strong>
              </div>
            ) : (
              <div className="password-form">
                <div className="form-group">
                  <label>Nouveau mot de passe</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Entrez un nouveau mot de passe"
                  />
                </div>

                <div className="form-group">
                  <label>Confirmer le mot de passe</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirmez le mot de passe"
                  />
                </div>

                <div className="password-requirements">
                  <h4>Le mot de passe doit contenir :</h4>
                  <ul>
                    <li className={hasLowerCase ? 'valid' : 'invalid'}>
                      {hasLowerCase ? <CheckIcon /> : <CrossIcon />}
                      Une lettre minuscule
                    </li>
                    <li className={hasUpperCase ? 'valid' : 'invalid'}>
                      {hasUpperCase ? <CheckIcon /> : <CrossIcon />}
                      Une lettre majuscule
                    </li>
                    <li className={hasNumber ? 'valid' : 'invalid'}>
                      {hasNumber ? <CheckIcon /> : <CrossIcon />}
                      Un chiffre
                    </li>
                    <li className={hasSpecialChar ? 'valid' : 'invalid'}>
                      {hasSpecialChar ? <CheckIcon /> : <CrossIcon />}
                      Un caractère spécial
                    </li>
                    <li className={passwordsMatch ? 'valid' : 'invalid'}>
                      {passwordsMatch ? <CheckIcon /> : <CrossIcon />}
                      Les mots de passe correspondent
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {error && <div className="error-message">{error}</div>}

            <div className="modal-actions">
              <Button
                value="Annuler"
                variant="secondary"
                onClick={onClose}
              />
              <Button
                value={isSubmitting ? "En cours..." : "Valider"}
                variant="primary"
                onClick={handleSubmit}
                disabled={resetOption === 'direct' && (!isPasswordValid || !passwordsMatch) || isSubmitting}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordModal;